import { TagSeverity } from '@/components/chariot-ui';

import { AuthenticatedRoute } from '@/app/constant';
import { useAuth } from '@/state/auth';

import { MONITORING_NAME_MAP } from './monitoringConstants';

/**
 * Configuration for monitoring navigation
 */
export interface MonitoringNavigationConfig {
  customerEmail?: string;
  baseRoute?: string;
}

/**
 * Custom hook for centralized monitoring navigation logic
 *
 * Handles customer impersonation and builds proper navigation URLs
 * with filters for vulnerabilities, assets, seeds, and integrations.
 *
 * @example
 * // Customer-specific navigation (with impersonation)
 * const navigation = useMonitoringNavigation({ customerEmail: 'user@example.com' });
 * navigation.navigateToVulnerabilities();
 *
 * @example
 * // Current user navigation (no impersonation)
 * const navigation = useMonitoringNavigation();
 * navigation.navigateToAssets({ attackSurface: 'cloud' });
 */
export const useMonitoringNavigation = (
  config?: MonitoringNavigationConfig
) => {
  const { startImpersonation } = useAuth();
  const customerEmail = config?.customerEmail;

  /**
   * Navigate to specified route, optionally with customer impersonation
   */
  const navigate = (route: string | AuthenticatedRoute) => {
    if (customerEmail) {
      startImpersonation(customerEmail, route as AuthenticatedRoute);
    } else {
      // Direct navigation for current user (implementation depends on router)
      window.location.href = route;
    }
  };

  /**
   * Navigate to vulnerabilities page with optional filters
   */
  const navigateToVulnerabilities = (filters?: {
    severity?: string;
    status?: string;
  }) => {
    let route: string = AuthenticatedRoute.VULNERABILITIES;

    if (filters) {
      const params = new URLSearchParams();
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.status) params.append('status', filters.status);

      const queryString = params.toString();
      if (queryString) {
        route = `${route}?${queryString}`;
      }
    }

    navigate(route);
  };

  /**
   * Navigate to vulnerability page with specific severity and status
   */
  const navigateToVulnerability = (
    severity: TagSeverity,
    status: 'O' | 'T'
  ) => {
    const severityMap: Record<TagSeverity, string> = {
      critical: 'C',
      high: 'H',
      medium: 'M',
      low: 'L',
      info: 'I',
      exposure: 'E',
    };

    const severityValue = severityMap[severity];
    const route = `${AuthenticatedRoute.VULNERABILITIES}?severity=${severityValue}&status=${status}`;
    navigate(route);
  };

  /**
   * Navigate to assets page with optional filters
   */
  const navigateToAssets = (filters?: Record<string, string | string[]>) => {
    let route: string = AuthenticatedRoute.ASSETS;

    if (filters && Object.keys(filters).length > 0) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v));
        } else {
          params.append(key, value);
        }
      });

      const queryString = params.toString();
      if (queryString) {
        route = `${route}?${queryString}`;
      }
    }

    navigate(route);
  };

  /**
   * Navigate to assets filtered by section key
   */
  const navigateToAssetSection = (sectionKey: string) => {
    let params = '';

    if (sectionKey === 'cloud' || sectionKey === MONITORING_NAME_MAP['cloud']) {
      params = 'attackSurface=cloud';
    } else if (
      sectionKey === 'externalNetwork' ||
      sectionKey === MONITORING_NAME_MAP['externalNetwork']
    ) {
      params = 'attackSurface=external';
    } else if (
      sectionKey === 'internalNetwork' ||
      sectionKey === MONITORING_NAME_MAP['internalNetwork']
    ) {
      params = 'attackSurface=internal';
    } else if (
      sectionKey === 'applications' ||
      sectionKey === MONITORING_NAME_MAP['applications']
    ) {
      params = 'search=webapplication';
    } else if (
      sectionKey === 'code' ||
      sectionKey === MONITORING_NAME_MAP['code']
    ) {
      params = 'source=github&source=gitlab&source=bitbucket';
    }

    if (params) {
      const route = `${AuthenticatedRoute.ASSETS}?${params}`;
      navigate(route);
    }
  };

  /**
   * Navigate to assets filtered by category and section
   */
  const navigateToAssetCategory = (categoryKey: string, sectionKey: string) => {
    let params = '';

    // Cloud providers - use source filter
    if (categoryKey === MONITORING_NAME_MAP['amazon']) {
      params = 'source=amazon';
    } else if (categoryKey === MONITORING_NAME_MAP['azure']) {
      params = 'source=azure';
    } else if (categoryKey === MONITORING_NAME_MAP['gcp']) {
      params = 'source=gcp';
    } else if (categoryKey === MONITORING_NAME_MAP['digitalocean']) {
      params = 'source=digitalocean';
    }
    // Code repositories - use source filter
    else if (categoryKey === MONITORING_NAME_MAP['github']) {
      params = 'source=github';
    } else if (categoryKey === MONITORING_NAME_MAP['gitlab']) {
      params = 'source=gitlab';
    } else if (categoryKey === MONITORING_NAME_MAP['bitbucket']) {
      params = 'source=bitbucket';
    }
    // Perimeter Network - use attackSurface=external + search for class
    else if (sectionKey === MONITORING_NAME_MAP['externalNetwork']) {
      if (categoryKey === MONITORING_NAME_MAP['ipv4']) {
        params = 'attackSurface=external&search=ipv4';
      } else if (categoryKey === MONITORING_NAME_MAP['ipv6']) {
        params = 'attackSurface=external&search=ipv6';
      } else if (categoryKey === MONITORING_NAME_MAP['domain']) {
        params = 'attackSurface=external&search=domain';
      } else if (categoryKey === MONITORING_NAME_MAP['subdomain']) {
        params = 'attackSurface=external&search=subdomain';
      }
    }
    // Internal Network - use attackSurface=internal + search for class
    else if (sectionKey === MONITORING_NAME_MAP['internalNetwork']) {
      if (categoryKey === MONITORING_NAME_MAP['ipv4']) {
        params = 'attackSurface=internal&search=ipv4';
      } else if (categoryKey === MONITORING_NAME_MAP['ADDomain']) {
        params = 'attackSurface=internal&search=addomain';
      } else if (categoryKey === MONITORING_NAME_MAP['ADUser']) {
        params = 'attackSurface=internal&search=aduser';
      } else if (categoryKey === MONITORING_NAME_MAP['ADComputer']) {
        params = 'attackSurface=internal&search=adcomputer';
      }
    }
    // Applications - use class search
    else if (sectionKey === MONITORING_NAME_MAP['applications']) {
      if (categoryKey === MONITORING_NAME_MAP['webapplication']) {
        params = 'search=webapplication';
      } else if (categoryKey === MONITORING_NAME_MAP['webseed']) {
        params = 'search=webseed';
      }
    }

    if (params) {
      const route = `${AuthenticatedRoute.ASSETS}?${params}`;
      navigate(route);
    }
  };

  /**
   * Navigate to seeds page with optional filters
   */
  const navigateToSeeds = (filters?: Record<string, string>) => {
    let route: string = AuthenticatedRoute.SEEDS;

    if (filters && Object.keys(filters).length > 0) {
      const params = new URLSearchParams(filters);
      const queryString = params.toString();
      if (queryString) {
        route = `${route}?${queryString}`;
      }
    }

    navigate(route);
  };

  /**
   * Navigate to seeds filtered by type and status
   */
  const navigateToSeedType = (type: string, status: 'approved' | 'pending') => {
    const route = `${AuthenticatedRoute.SEEDS}?type=${type}&status=${status}`;
    navigate(route);
  };

  /**
   * Navigate to integrations page
   */
  const navigateToIntegrations = () => {
    navigate(AuthenticatedRoute.INTEGRATIONS);
  };

  /**
   * Navigate to jobs page with optional filters
   */
  const navigateToJobs = (filters?: Record<string, string>) => {
    let route: string = AuthenticatedRoute.JOBS;

    if (filters && Object.keys(filters).length > 0) {
      const params = new URLSearchParams(filters);
      const queryString = params.toString();
      if (queryString) {
        route = `${route}?${queryString}`;
      }
    }

    navigate(route);
  };

  return {
    navigateToVulnerabilities,
    navigateToVulnerability,
    navigateToAssets,
    navigateToAssetSection,
    navigateToAssetCategory,
    navigateToSeeds,
    navigateToSeedType,
    navigateToIntegrations,
    navigateToJobs,
  };
};
